/**
 * g18-batch: one card per proposed plan (`viko_task_create_batch`) in a project's Needs you
 * section. The summary line carries every number (tasks, waves, critical path, tiers), each
 * labelled; tap it for the waves with every task's tier, reason, dependencies and shared files.
 * "Approve plan" readies all its tasks in one request; "Review individually" approves one by one.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import { Check, ListChecks } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { type PlanCardModel, type PlanCardTask, planCards, planSummary } from '../lib/plan-card.js'
import { useTasks } from '../lib/queries.js'
import { Badge, errorText, useToast } from './ui.js'

const TIER_LABEL = { fast: 'Fast tier', standard: 'Standard tier', deep: 'Deep tier' } as const

function TaskLine({
  t,
  onOpenTask,
  action,
}: {
  t: PlanCardTask
  onOpenTask: (id: string) => void
  action?: React.ReactNode
}) {
  const after = t.deps.map((d) => d.title)
  return (
    <li className="flex items-start gap-2 py-1" data-testid={`plan-task-${t.ref}`}>
      <span className="grid min-w-0 flex-1 gap-0.5">
        <button
          type="button"
          className="justify-self-start text-left text-text-1 underline-offset-2 hover:underline"
          onClick={() => onOpenTask(t.id)}
        >
          {t.title}
        </button>
        <span className="text-text-2">
          {t.tier ? TIER_LABEL[t.tier] : 'No tier set'}
          {t.modelReason ? ` · ${t.modelReason}` : ''}
          {t.risk === 'high' ? ' · high risk' : ''}
        </span>
        <span className="text-text-3">
          {after.length ? `After: ${after.join(', ')}` : 'No dependencies'}
        </span>
        {t.files.length ? (
          <span className="break-words font-mono text-text-3">Files: {t.files.join(', ')}</span>
        ) : null}
      </span>
      {action}
    </li>
  )
}

export function PlanCardView({
  card,
  busy,
  onApprovePlan,
  onApproveTask,
  onOpenTask,
}: {
  card: PlanCardModel
  busy?: boolean
  onApprovePlan: () => void
  onApproveTask: (id: string) => void
  onOpenTask: (id: string) => void
}) {
  const [reviewing, setReviewing] = useState(false)
  return (
    <section
      className="mx-4 my-2 grid gap-2 rounded-xl bg-surface-2 p-3 text-[14px]"
      aria-label={`Plan: ${card.title}`}
      data-testid="plan-card"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ListChecks size={14} aria-hidden="true" className="text-accent-text" />
        <h2 className="m-0 min-w-0 flex-1 text-[15px] font-semibold">Plan: {card.title}</h2>
        <Badge tone="accent">Proposed</Badge>
      </div>
      <details data-testid="plan-details">
        <summary className="min-h-11 cursor-pointer py-2 text-text-2">
          <span data-testid="plan-summary">{planSummary(card)}</span>
        </summary>
        <div className="grid gap-3 pb-2">
          {card.doc ? (
            <p className="m-0 text-text-2">
              Plan doc: <code className="break-words font-mono">{card.doc}</code>
            </p>
          ) : null}
          <ol className="m-0 grid list-none gap-3 p-0" aria-label="Waves">
            {card.waves.map((wave, i) => (
              <li key={wave[0]?.id ?? i}>
                <p className="m-0 font-medium text-text-1">
                  Wave {(i + 1).toLocaleString()} · {wave.length.toLocaleString()}{' '}
                  {wave.length === 1 ? 'task' : 'tasks'} in parallel
                </p>
                <ul className="m-0 list-none p-0">
                  {wave.map((t) => (
                    <TaskLine key={t.id} t={t} onOpenTask={onOpenTask} />
                  ))}
                </ul>
              </li>
            ))}
          </ol>
          {card.conflicts.length ? (
            <div>
              <p className="m-0 font-medium text-text-1">
                Shared files ({card.conflicts.length.toLocaleString()}): these run one after the
                other
              </p>
              <ul className="m-0 list-none p-0" aria-label="File conflicts">
                {card.conflicts.map((c) => (
                  <li key={`${c.a}|${c.b}`} className="break-words py-0.5 text-text-2">
                    {c.a} and {c.b}: <span className="font-mono">{c.files.join(', ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="m-0 text-text-3">No two tasks share a file.</p>
          )}
        </div>
      </details>
      {reviewing ? (
        <ul className="m-0 list-none p-0" aria-label="Review tasks individually">
          {card.waves.flat().map((t) => (
            <TaskLine
              key={t.id}
              t={t}
              onOpenTask={onOpenTask}
              action={
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => onApproveTask(t.id)}
                >
                  Approve
                </Button>
              }
            />
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={() => setReviewing((r) => !r)}>
          {reviewing ? 'Hide task list' : 'Review individually'}
        </Button>
        <Button
          size="sm"
          variant="primary"
          icon={<Check size={14} />}
          disabled={busy}
          onClick={onApprovePlan}
        >
          Approve plan ({card.taskIds.length.toLocaleString()})
        </Button>
      </div>
    </section>
  )
}

/** The project's proposed plans, decrypted and grouped here (E2E titles never reach the hub). */
export function PlanCards({ projectId }: { projectId: string }) {
  const tasks = useTasks({ projectId })
  const cards = useMemo(() => planCards(tasks.data ?? []), [tasks.data])
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const nav = useNavigate()
  const [busy, setBusy] = useState(false)
  if (!cards.length) return null
  const run = async (fn: () => Promise<string>) => {
    setBusy(true)
    try {
      toast(await fn())
      await qc.invalidateQueries({ queryKey: ['tasks'] })
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      {cards.map((c) => (
        <PlanCardView
          key={c.planId}
          card={c}
          busy={busy}
          onOpenTask={(id) => void nav({ to: '/t/$taskId', params: { taskId: id } })}
          // One request readies the whole plan (only the tasks shown on this card).
          onApprovePlan={() =>
            void run(async () => {
              const r = await b.api.approvePlan(c.planId, { taskIds: c.taskIds })
              return `Approved ${r.approved.length.toLocaleString()} task${r.approved.length === 1 ? '' : 's'}`
            })
          }
          onApproveTask={(id) =>
            void run(async () => {
              await b.api.approveTask(id)
              return 'Approved'
            })
          }
        />
      ))}
    </>
  )
}
